'use client'

import type { InputWithValidation } from '../../../types/EventDetailTypes'
import { captureEventAndMarkTargetAsInputWithCustomValidation } from '../../../helpers/captureEventAndMarkTargetAsInputWithCustomValidation'
import { getArrayOfValidatableInputsFromSource } from '../../../helpers/getArrayOfValidatableInputsFromSource'
import { useEffect } from 'react'

/**
 * @memberof useValidateSchema
 *
 * @description takes all individual input elements, related to the provided form element and
 * captures events on elements that have custom validations. When captured, those elements
 * are tagged with hasCustomValidations
 *
 * @listens RequestPartialValidation
 * */
export const useCaptureEventsOnIndividualInputsAndMarkTargetAsInputWithCustomValidation = (
    formElement: HTMLFormElement | null,
    fieldsWithCustomValidation: string[] | undefined,
) => {
    useEffect(() => {
        if (!formElement) {
            return
        }
        const inputs = getArrayOfValidatableInputsFromSource(formElement)
        if (!inputs) {
            return
        }
        const controller = new AbortController()
        const signal = controller.signal
        inputs.forEach((input: InputWithValidation) => {
            if (!fieldsWithCustomValidation?.some((fieldName: string) => fieldName === input.name)) {
                return
            }
            captureEventAndMarkTargetAsInputWithCustomValidation(input, signal)
        })
        return () => {
            controller.abort()
        }
    }, [formElement])
}
